import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import React from "react";
import { Button } from "../../button";
import { useTableFilters, useTableQueryArgument } from "../data-table-context";
import { TbFilter } from "react-icons/tb";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { CLEAR_FLAG } from "@/lib/clear-flag";
import { cn } from "@/lib/utils";
import { createEventsContext, useEvents } from "../events-context";
import { functionalUpdate } from "@tanstack/react-table";
import { ScrollArea } from "@/components/ui/scroll-area";

export function DataTableFiltersFormProvider({
  children,
}: {
  children?: React.ReactNode;
}) {
  const [args] = useTableQueryArgument();
  const { defaultFiltersApply } = useTableFilters();
  const form = useForm({});
  React.useEffect(() => {
    form.reset({ ...defaultFiltersApply, ...args });
  }, [args, defaultFiltersApply]);
  return <FormProvider {...form}>{children}</FormProvider>;
}

export function DataTableFiltersFormProviderConditional({
  children,
  omit,
}: {
  omit?: boolean;
  children?: React.ReactNode;
}) {
  if (omit) return children;
  return <DataTableFiltersFormProvider children={children} />;
}

const Events = createEventsContext<{ onClose: () => void }>();

export function DataTableFiltersPopover({
  omit,
  filters: Filters,
  children,
}: {
  omit?: boolean;
  filters?: React.FC;
  children?: React.ReactNode;
}) {
  const { isFiltersActive } = useTableFilters();
  const [open, _setOpen] = React.useState(false);
  const events = Events.useEventsState();
  const setOpen: typeof _setOpen = React.useCallback(
    (a) =>
      _setOpen((s) => {
        const next = functionalUpdate(a, s);
        if (next === false) {
          events.emit("onClose");
        }
        return next;
      }),
    [_setOpen, events],
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger data-component="data-table-filters" asChild>
        <Button
          // variant={isFiltersActive ? "midnight" : undefined}
          className={cn(
            "flex gap-1 justify-around",
            isFiltersActive && "text-azure hover:text-azure hover:bg-ghost",
            // open && "ring-1 ring-azure",
          )}
        >
          <TbFilter className="w-4 h-4" />
          {"All Filters"}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="mt-2 max-w-[var(--radix-popover-content-available-width)] w-[650px]"
      >
        <DataTableFiltersFormProviderConditional omit={omit}>
          {children ?? (
            <Events.Provider value={events}>
              <DataTableFiltersContent onClose={() => setOpen(false)}>
                {Filters && (
                  <ScrollArea
                    orientation="vertical"
                    className="-mx-4 max-h-[calc(var(--radix-popover-content-available-height)-8rem)]"
                    viewport={{
                      className:
                        "px-4 pb-1 max-h-[calc(var(--radix-popover-content-available-height)-8rem)]",
                    }}
                  >
                    <div
                      data-component="data-table-filters-content"
                      className="grid grid-cols-1 md:grid-cols-2 gap-2"
                    >
                      <Filters />
                    </div>
                  </ScrollArea>
                )}
              </DataTableFiltersContent>
            </Events.Provider>
          )}
        </DataTableFiltersFormProviderConditional>
      </PopoverContent>
    </Popover>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useDataTableFiltersSubmit() {
  const [_args, setArgs] = useTableQueryArgument();
  const onSubmit = React.useCallback(
    async (data) => {
      setArgs({ ...data, page: 1, [CLEAR_FLAG]: true });
    },
    [setArgs],
  );
  const form = useFormContext();
  return React.useCallback(() => onSubmit(form.getValues()), [form]);
}

export function DataTableFiltersContent({
  onClose,
  children,
}: {
  onClose?: () => void;
  children?: React.ReactNode;
}) {
  const [args, setArgs] = useTableQueryArgument();
  const { isFiltersActive, clearFilters: _clearFilters } = useTableFilters();
  const form = useFormContext();

  const submit = React.useCallback(
    (data) => setArgs({ ...data, page: 1, [CLEAR_FLAG]: true }),
    [setArgs],
  );

  const onSubmit = React.useCallback(
    async (data) => {
      submit(data);
      if (onClose) onClose();
    },
    [submit, onClose],
  );

  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;
  const isDirtyRef = React.useRef(isDirty);
  React.useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirtyRef, isDirty]);

  // Clear and Cancel both close the popover, and closing a dirty popover auto-applies the form
  // (see the "onClose" listener below). The form still holds the pre-clear values -- it is only
  // reset by an effect once the new `args` land -- so without dropping the dirty flag first, that
  // auto-apply fires in the same tick and puts the filters the user just cleared straight back
  // (ID-6913). An explicit Clear/Cancel outranks the close-to-apply shortcut.
  const clearFilters = React.useCallback(() => {
    isDirtyRef.current = false;
    if (_clearFilters) _clearFilters();
    if (onClose) onClose();
  }, [_clearFilters, onClose]);

  const events = useEvents();
  React.useEffect(
    () =>
      events.on(
        "onClose",
        () => isDirtyRef.current && submit(form.getValues()),
      ),
    [form],
  );

  return (
    <form
      tabIndex={0}
      onSubmit={form.handleSubmit(onSubmit)}
      onKeyDown={(ev) => {
        if (ev.key === "Enter") {
          ev.preventDefault();
          onSubmit(form.getValues());
        }
        ev.stopPropagation();
      }}
    >
      <div>
        <div className="flex gap-2">
          {"Filter"}
          <div className="ml-auto flex items-center gap-2">
            <Button
              data-component="data-table-filters-clear"
              type="button"
              disabled={!isFiltersActive}
              onClick={clearFilters}
            >
              {"Clear"}
            </Button>
          </div>
        </div>
        {children}
        <div className="flex flex-row-reverse align-center pt-4 gap-2 w-full">
          <Button
            data-component="data-table-filters-apply"
            disabled={!isDirty}
            variant={"azure"}
          >
            {"Apply"}
          </Button>
          <Button
            data-component="data-table-filters-cancel"
            disabled={!isDirty}
            type="button"
            onClick={(_ev) => {
              isDirtyRef.current = false;
              form.reset(args, { keepDefaultValues: false });
              if (onClose) onClose();
            }}
          >
            {"Cancel"}
          </Button>
        </div>
      </div>
    </form>
  );
}

export const DataTableFilters = DataTableFiltersPopover;
